import { CACHE_TIME } from '@/const/cache';

import {
  VivrelleQuery,
  SearchQueryResponse,
  RelatedQuery,
  RawAlgoliaProduct,
  SearchRevolveQueryResponse
} from '@/types/Search';
import { ProductCardData } from '@/types/Product';
import { algoliaResultToProductCardImages } from './image';
import { CLOSET_SLOT_TIER_SLUG } from '@/types/Closet';

export async function runSearch(
  query: VivrelleQuery,
  cacheTime: CACHE_TIME = CACHE_TIME.NO_CACHE
): Promise<SearchQueryResponse> {
  const searchBaseUrl = process.env.NEXT_PUBLIC_ALGOLIA_MICROSERVICE_BASE_URL;

  try {
    const response = await fetch(searchBaseUrl + '/api/v1/search', {
      method: 'POST',
      body: JSON.stringify(query),
      mode: 'cors',
      next: {
        revalidate: cacheTime
      },
      headers: {
        'content-type': 'application/json'
      }
    });
    const json = await response.json();
    return json as SearchQueryResponse;
  } catch (error: unknown) {
    console.error('[runSearch] ERROR:', error);
    throw error;
  }
}

export async function getRelatedProducts(
  query: RelatedQuery
): Promise<SearchQueryResponse> {
  const searchBaseUrl = process.env.NEXT_PUBLIC_ALGOLIA_MICROSERVICE_BASE_URL;
  try {
    const response = await fetch(searchBaseUrl + '/api/v1/related', {
      method: 'POST',
      body: JSON.stringify(query),
      mode: 'cors',
      next: {
        revalidate: CACHE_TIME.ONE_DAY
      },
      headers: {
        'content-type': 'application/json'
      }
    });
    const json = await response.json();
    return json as SearchQueryResponse;
  } catch (error: unknown) {
    console.error('[getRelatedProducts] ERROR:', error);
    throw error;
  }
}

/** Turns the raw Algolia result into data for the `<ProductCard>` component */
export const algoliaResultToProductCard = (
  algoliaProduct: RawAlgoliaProduct
): ProductCardData => {
  const { mainImage, hoverImage } =
    algoliaResultToProductCardImages(algoliaProduct);

  const tier = algoliaProduct['subscription-level'];

  return {
    id: algoliaProduct.objectID,
    borrowed: algoliaProduct.ep_inventory_count === 0,
    brand: algoliaProduct.ep_brand,
    name: algoliaProduct.ep_name,
    slug: algoliaProduct.ep_slug,
    sku: algoliaProduct.ep_sku,
    tier,
    mainImage,
    hoverImage,
    item_group_id: algoliaProduct.ep_sku,
    showWishlist: tier !== 'Privee'
  };
};

export async function getGenericSearch(
  query: VivrelleQuery,
  cacheTime: CACHE_TIME = CACHE_TIME.NO_CACHE
): Promise<SearchQueryResponse> {
  const searchBaseUrl = process.env.NEXT_PUBLIC_ALGOLIA_MICROSERVICE_BASE_URL;

  try {
    const response = await fetch(searchBaseUrl + '/api/v1/generic-search', {
      method: 'POST',
      body: JSON.stringify(query),
      mode: 'cors',
      next: {
        revalidate: cacheTime
      },
      headers: {
        'content-type': 'application/json'
      }
    });
    const json = await response.json();
    return json as SearchQueryResponse;
  } catch (error: unknown) {
    console.error('[runSearch] ERROR:', error);
    throw error;
  }
}

// Helper function to retrieve available facet values
export async function getAvailableFacets(): Promise<{
  vivrelle: { [key: string]: string[] };
  revolve: { [key: string]: string[] };
}> {
  const vivrelleQuery: VivrelleQuery = {
    facets: ['ep_brand', 'ep_color', 'ep_occasion']
  };

  const revolveQuery: GenericSearchQuery = {
    index: 'uat_REVOLVE_FWRD_PRODUCTS',
    searchParams: {
      facets: [
        'common_color',
        'brand',
        'category',
        'sub_category',
        'sub_sub_category'
      ]
    }
  };

  try {
    const [vivrelleResponse, revolveResponse] = await Promise.all([
      runSearch(vivrelleQuery, CACHE_TIME.ONE_DAY),
      runGenericFacetsSearch(revolveQuery, CACHE_TIME.ONE_DAY)
    ]);

    if (!vivrelleResponse.success || !vivrelleResponse.data?.facets) {
      throw new Error('Failed to fetch vivrelle facets from search API');
    }

    if (!revolveResponse.success || !revolveResponse.data?.facets) {
      throw new Error('Failed to fetch revolve facets from search API');
    }

    const facetValues: {
      vivrelle: { [key: string]: string[] };
      revolve: { [key: string]: string[] };
    } = {
      vivrelle: {
        ep_brand: Object.keys(vivrelleResponse.data.facets['ep_brand'] || {}),
        ep_color: Object.keys(vivrelleResponse.data.facets['ep_color'] || {}),
        ep_occasion: Object.keys(
          vivrelleResponse.data.facets['ep_occasion'] || {}
        )
      },
      revolve: {
        brand: Object.keys(revolveResponse.data.facets['brand'] || {}),
        common_color: Object.keys(
          revolveResponse.data.facets['common_color'] || {}
        ),
        category: Object.keys(revolveResponse.data.facets['category'] || {}),
        sub_category: Object.keys(
          revolveResponse.data.facets['sub_category'] || {}
        ),
        sub_sub_category: Object.keys(
          revolveResponse.data.facets['sub_sub_category'] || {}
        )
      }
    };
    return facetValues;
  } catch (error) {
    return {
      vivrelle: { ep_brand: [], ep_color: [], ep_occasion: [] },
      revolve: {
        brand: [],
        common_color: [],
        category: [],
        sub_category: [],
        sub_sub_category: []
      }
    };
  }
}

export interface GenericSearchQuery {
  index: string;
  query?: string;
  searchParams?: {
    filters?: string;
    hitsPerPage?: number;
    page?: number;
    facets?: string[];
  };
}

export async function runGenericSearch(
  query: GenericSearchQuery,
  cacheTime: CACHE_TIME = CACHE_TIME.NO_CACHE
): Promise<SearchRevolveQueryResponse> {
  const searchBaseUrl = process.env.NEXT_PUBLIC_ALGOLIA_MICROSERVICE_BASE_URL;

  if (query.searchParams?.filters) {
    const filterParts = query.searchParams.filters.split(' AND ');

    const escapedFilterParts = filterParts.map((part) => {
      const [attribute, valueWithQuotes] = part.split(':');
      const escapedAttribute = attribute.includes(' ')
        ? `\`${attribute}\``
        : attribute;
      return `${escapedAttribute}:${valueWithQuotes}`;
    });

    query.searchParams.filters = escapedFilterParts.join(' OR ');
  }
  // console.log('[runGenericSearch] Query:', query);

  try {
    const response = await fetch(`${searchBaseUrl}/api/v1/generic-search`, {
      method: 'POST',
      body: JSON.stringify(query),
      mode: 'cors',
      next: {
        revalidate: cacheTime
      },
      headers: {
        'content-type': 'application/json'
      }
    });
    const json = await response.json();
    // console.log('[runGenericSearch] Response:', json);

    return json as SearchRevolveQueryResponse;
  } catch (error: unknown) {
    console.error('[runGenericSearch] ERROR:', error);
    throw error;
  }
}

/**
 * Fetches live products from Algolia
 * @param limit Maximum number of products to fetch (max 1000)
 * @param cacheTime Cache duration in seconds
 * @returns Array of live products
 */
export async function fetchInStockProducts(
  limit: number = 100,
  cacheTime: CACHE_TIME = CACHE_TIME.NO_CACHE,
  subscriptionLevel?: CLOSET_SLOT_TIER_SLUG[]
): Promise<RawAlgoliaProduct[]> {
  let newQuery: VivrelleQuery = {
    inStock: true,
    collection: {
      collectionType: ['Handbags']
    },
    hitsCount: limit
  };

  if (subscriptionLevel) {
    newQuery.subscriptionLevel = subscriptionLevel;
  }

  try {
    const result = await runSearch(newQuery, cacheTime);
    const hits = result.data?.hits;

    return hits || [];
  } catch (error) {
    console.error('Error fetching live products:', error);
    return [];
  }
}

export async function runGenericFacetsSearch(
  query: GenericSearchQuery,
  cacheTime: CACHE_TIME = CACHE_TIME.NO_CACHE
): Promise<SearchQueryResponse> {
  const searchBaseUrl = process.env.NEXT_PUBLIC_ALGOLIA_MICROSERVICE_BASE_URL;

  if (query.searchParams?.filters) {
    const filterParts = query.searchParams.filters.split(' AND ');

    const escapedFilterParts = filterParts.map((part) => {
      const [attribute, valueWithQuotes] = part.split(':');
      const escapedAttribute = attribute.includes(' ')
        ? `\`${attribute}\``
        : attribute;
      return `${escapedAttribute}:${valueWithQuotes}`;
    });

    query.searchParams.filters = escapedFilterParts.join(' AND ');
  }
  try {
    const response = await fetch(`${searchBaseUrl}/api/v1/generic-facets`, {
      method: 'POST',
      body: JSON.stringify(query),
      mode: 'cors',
      next: {
        revalidate: cacheTime
      },
      headers: {
        'content-type': 'application/json'
      }
    });
    const json = await response.json();
    return json as SearchQueryResponse;
  } catch (error: unknown) {
    console.error('[runGenericFacetsSearch] ERROR:', error);
    throw error;
  }
}
